"use client";

import React, { useEffect, useRef, useState } from "react";
import useTemplateBase, {
  mergePropertyListsWithCurrent,
  resolvePropertyListName,
} from "./TemplateBase";
import { Button } from "@/components/ui/button";
import { ImageCropModal } from "@/components/ui/ImageCropModal";
import {
  MapPin,
  Bed,
  Bath,
  Square,
  Phone,
  Mail,
  Upload,
  Image as ImageIcon,
  Crop,
  Tag,
  X,
  Check,
  Loader2,
  Building2,
  IdCard
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import profile from "../../../public/profilePicture/profile.webp";
import openHouseBadge from "../../assets/star_red.png";

interface LoggedUser {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone_number?: string;
  image?: string;
  avatar?: string;
}

interface Property {
  id: number;
  propertyListType?: {
    id: number;
    list_name: string;
    custom_template_data?: Record<string, any>;
  };
  title: string;
  listing_id: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  price?: number | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  area?: number | null;
  description?: string | null;
  text_section?: string | null;
  image?: string | null;
  original_image?: string | null;
  photos?: string[] | null;
  is_open_house?: boolean;
}

interface PropertyList {
  id: number;
  list_name: string;
}

interface KeyFeature {
  id?: number;
  feature_name: string;
  propetyId: number;
}

interface Template5Props {
  property?: Property | null;
  propertyLists?: PropertyList[];
  keyFeatures?: KeyFeature[];
  onDirtyChange?: (dirty: boolean) => void;
  onChange?: (updatedFields: Partial<Template5FlyerData>) => void;
  onPropertyListChange?: (selectedList: string) => void;
  selectedPropertyList?: string;
  onPropertyListCreated?: (newList: PropertyList) => void;
  selectedList?: string;
  readOnly?: boolean;
}

interface FeatureItem {
  id?: number;
  feature_name: string;
}

interface Template5FlyerData {
  price: number | null;
  address: string;
  city?: string;
  state?: string;
  bedrooms: number | null;
  bathrooms: number | null;
  sqft: number | null;
  area?: number | null;
  description: string;
  headline: string;
  features: FeatureItem[];
  heroImage: string;
  secondaryImage?: string;
  photos?: string[];
  list_name?: string;
  agentName?: string;
  agentPhone?: string;
  agentEmail?: string;
  company?: string;
}


const boxHeroRes = "350px X 550px";
const RESOLUTION_HINT = `For best results, upload an image that is exactly ${boxHeroRes}. If your image is larger, it will be automatically resized to fit, but using the recommended resolution ensures optimal quality and layout.`;

// ─── Helper: route already-saved (non data:/blob:) S3 image URLs through the
// backend image proxy before feeding them to the crop modal's <canvas>, so the
// export doesn't fail with a cross-origin "tainted canvas" error. Mirrors the
// same shouldProxyCaptureImage/getCaptureImageUrl pattern already used for
// html2canvas capture in CampaignBuilder.tsx.
const getEditableImageUrl = (url: string): string => {
  if (!url || url.startsWith("data:") || url.startsWith("blob:")) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    const needsProxy =
      parsed.hostname.includes("amazonaws.com") ||
      parsed.hostname.includes("mylistiq-production.s3");
    if (!needsProxy) return url;
    return `${endpoints.image_proxy}?url=${encodeURIComponent(url)}`;
  } catch {
    return url;
  }
};

// ─── Helper: format price with auto-scaling font ──────────────────────────────
const formatPrice = (price: number | null): string => {
  if (price == null) return "$0";
  return `$${Number(price).toLocaleString()}`;
};

// ─── Helper: derive responsive font size class based on digit count ───────────
const getPriceFontClass = (price: number | null): string => {
  const str = price != null ? String(Math.round(price)) : "0";
  const digits = str.replace(/\D/g, "").length;
  if (digits <= 6) return "text-4xl";
  if (digits <= 8) return "text-3xl";
  if (digits <= 10) return "text-2xl";
  return "text-xl";
};

export default function Template1({
  property,
  propertyLists = [],
  keyFeatures = [],
  onDirtyChange,
  onChange,
  onPropertyListChange,
  selectedPropertyList: externalSelectedList,
  selectedList: viewSelectedList,
  onPropertyListCreated,
  readOnly = false,
}: Template5Props) {
  const { toast } = useToast();

  const [cropModal, setCropModal] = useState<{ open: boolean; imageUrl: string }>({
    open: false,
    imageUrl: "",
  });

  // Tracks the untouched "original" hero photo for this editing session, so
  // Edit Image always re-crops from the full source rather than whatever is
  // currently displayed. Seeded from property.original_image, but kept in
  // local state (not re-read from the prop after that) because the parent's
  // `property` object isn't refreshed after the background PATCH in
  // handleFileChange/openEditImage's self-heal — without this, a second
  // Edit Image later in the same session could wrongly re-seed itself from
  // the already-cropped current display instead of the true original.
  const [originalHeroImage, setOriginalHeroImage] = useState<string>(
    property?.original_image || ""
  );

  const [isCustomList, setIsCustomList] = useState(false);
  const [customListName, setCustomListName] = useState("");
  const [isSavingCustomList, setIsSavingCustomList] = useState(false);
  const [localPropertyLists, setLocalPropertyLists] =
    useState<PropertyList[]>(propertyLists);
  const [localSelectedListOverride, setLocalSelectedListOverride] = useState<
    string | null
  >(null);

  const {
    flyerData,
    setFlyerData,
    selectedPropertyList,
    fileInputRef,
    agentData,
    handleInputChange,
    addFeature,
    handleFeatureChange,
    handleFeatureBlur,
    handleFeatureDelete,
    updatePropertyList,
    getCustomTemplateData,
    saveCustomTemplateData,
  } = useTemplateBase({
    property,
    propertyLists: localPropertyLists,
    keyFeatures,
    selectedPropertyList: externalSelectedList,
    onDirtyChange,
    onChange,
    onPropertyListChange,
  });

  useEffect(() => {
    setLocalPropertyLists(propertyLists);
  }, [propertyLists]);

  useEffect(() => {
    setLocalSelectedListOverride(null);
  }, [property?.id]);

  useEffect(() => {
    setOriginalHeroImage(property?.original_image || "");
  }, [property?.id]);

  const existingCustomData =
    property?.propertyListType?.custom_template_data?.template1 || [];
  const [customFields, setCustomFields] = useState<
    { key: string; value: string }[]
  >(Array.isArray(existingCustomData) ? existingCustomData : []);
  const [editingCustom, setEditingCustom] = useState(false);

  const addCustomField = () => {
    if (readOnly) return;
    setCustomFields((prev) => [...prev, { key: "", value: "" }]);
  };

  const updateCustomField = (
    idx: number,
    field: "key" | "value",
    val: string
  ) => {
    if (readOnly) return;
    setCustomFields((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: val };
      return next;
    });
  };

  const removeCustomField = (idx: number) => {
    if (readOnly) return;
    setCustomFields((prev) => prev.filter((_, i) => i !== idx));
  };

  const saveCustomForTemplate = async () => {
    if (readOnly) return;
    try {
      if (typeof saveCustomTemplateData === "function") {
        await saveCustomTemplateData(
          "template1",
          customFields.filter((f) => f.key || f.value)
        );
      } else {
        const token = localStorage.getItem("access_token");
        if (property?.propertyListType?.id) {
          await axiosInstance.patch(
            `${endpoints.propertylisttype}${property.propertyListType.id}/`,
            {
              custom_template_data: {
                ...(property.propertyListType.custom_template_data || {}),
                template1: customFields,
              },
            },
            {
              headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            }
          );
        }
      }
      setEditingCustom(false);
      toast({ title: "Saved", description: "Custom fields saved" });
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: "Failed saving custom fields",
        variant: "destructive",
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (readOnly) return;
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid File", description: "Please upload an image file", variant: "destructive" });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File Too Large", description: "Image must be less than 10 MB", variant: "destructive" });
      return;
    }
    const reader = new FileReader();
    reader.onloadend = async () => {
      const result = reader.result as string;
      try {
        const imgEl: HTMLImageElement = await new Promise((resolve, reject) => {
          const tmp = new Image();
          tmp.onload = () => resolve(tmp);
          tmp.onerror = reject;
          tmp.src = result;
        });
        const w = imgEl.naturalWidth;
        const h = imgEl.naturalHeight;

        if (boxHeroRes) {
          const match = boxHeroRes.match(/(\d+)\s*px\s*[xX]\s*(\d+)\s*px/i);

          if (match) {
            const rw = Number(match[1]);
            const rh = Number(match[2]);
            const hasWrongResolution = w !== rw || h !== rh;

            if (hasWrongResolution) {
              setTimeout(() => {
                toast({
                  title: "Resolution Suggestion",
                  description: `Uploaded image is ${w}×${h}. Recommended: ${rw}×${rh}.`,
                  duration: 5000,
                });
              }, 300);
            }
          }
        }
      } catch {
        // Ignore resolution-read failures and continue.
      }
      setFlyerData((prev) => ({ ...prev, heroImage: result }));
      // This freshly-picked photo becomes the new "original" for this session
      // immediately (don't wait on the background upload below), so Edit Image
      // always has an untouched source to re-crop from, right from this point on.
      setOriginalHeroImage(result);
      onDirtyChange?.(true);
      onChange?.({ heroImage: result });
      if (!(w !== rw || h !== rh)) {
        toast({
          title: "Image Uploaded",
          description: "Property image has been updated successfully",
        });
      }
    };
    reader.readAsDataURL(file);

    // Persist this freshly-picked photo as the untouched "original" separately
    // from the displayed image, so Edit Image can always re-crop from the full
    // source later instead of re-cropping an already-cropped result. This is
    // immediate and independent of the deferred "Save Changes" flow (same
    // pattern as gallery-photo saves), so it can't affect the existing save.
    if (property?.id) {
      const token = localStorage.getItem("access_token");
      const formData = new FormData();
      formData.append("original_image", file);
      axiosInstance
        .patch(`${endpoints.properties}${property.id}/`, formData, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "multipart/form-data",
          },
        })
        .catch((err) => {
          console.error("Failed to save original image:", err);
        });
    }
  };

  // ─── Edit Image: reposition/zoom the ORIGINAL hero photo (no file picker) ──
  const EDIT_CROP_W = 260;
  const EDIT_CROP_H = 409; // matches the 350x550 hero box aspect ratio

  const openEditImage = async () => {
    if (readOnly || !flyerData.heroImage) return;

    let originalSource = originalHeroImage;

    // This component remounts whenever the hero image changes (e.g. right
    // after Save), which resets local state — so an empty originalHeroImage
    // here doesn't necessarily mean "never captured", it may just mean this
    // mount hasn't loaded it. Check the server (unaffected by remounts)
    // before assuming there isn't one and falling back to the current
    // (possibly already-cropped) display.
    if (!originalSource && property?.id) {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axiosInstance.get(
          `${endpoints.properties}${property.id}/`,
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        originalSource = res?.data?.original_image || "";
      } catch (err) {
        console.error("Failed to check for existing original image:", err);
      }
    }

    if (!originalSource) {
      // Truly never captured yet (new property, or MLS import). Self-heal
      // from the current image so this and every future edit has a stable,
      // full source to re-crop from instead of the current display.
      originalSource = flyerData.heroImage;
      if (property?.id && !flyerData.heroImage.startsWith("data:")) {
        const token = localStorage.getItem("access_token");
        axiosInstance
          .patch(
            `${endpoints.properties}${property.id}/`,
            { original_image: flyerData.heroImage },
            { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
          )
          .catch((err) => {
            console.error("Failed to seed original image:", err);
          });
      }
    }

    setOriginalHeroImage(originalSource);
    setCropModal({ open: true, imageUrl: getEditableImageUrl(originalSource) });
  };

  const handleEditImageConfirm = (croppedDataUrl: string) => {
    setFlyerData((prev) => ({ ...prev, heroImage: croppedDataUrl }));
    onDirtyChange?.(true);
    onChange?.({ heroImage: croppedDataUrl });
    setCropModal({ open: false, imageUrl: "" });
  };

  const handleEditImageCancel = () => {
    setCropModal({ open: false, imageUrl: "" });
  };

  const handleSecondaryImageChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (readOnly) return;
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    if (!file.type.startsWith("image/")) {
      toast({ title: "Invalid File", description: "Please upload an image file", variant: "destructive" });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({ title: "File Too Large", description: "Image must be less than 10 MB", variant: "destructive" });
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;
      try {
        const imgEl: HTMLImageElement = await new Promise((resolve, reject) => {
          const tmp = new Image();
          tmp.onload = () => resolve(tmp);
          tmp.onerror = reject;
          tmp.src = dataUrl;
        });
        const w = imgEl.naturalWidth;
        const h = imgEl.naturalHeight;

        if (boxHeroRes) {
          const match = boxHeroRes.match(/(\d+)\s*px\s*[xX]\s*(\d+)\s*px/i);

          if (match) {
            const rw = Number(match[1]);
            const rh = Number(match[2]);

            const hasWrongResolution = w !== rw || h !== rh;

            if (hasWrongResolution) {
              setTimeout(() => {
                toast({
                  title: "Resolution Suggestion",
                  description: `Uploaded image is ${w}×${h}. Recommended: ${rw}×${rh}.`,
                  duration: 5000,
                });
              }, 300);
            }
          }
        }
      } catch {
        // Ignore resolution-read failures and continue.
      }
      setFlyerData((prev) => ({ ...prev, secondaryImage: dataUrl }));
      try {
        const token = localStorage.getItem("access_token");
        const formData = new FormData();
        formData.append("image", file);
        const uploadRes = await axiosInstance.post(endpoints.mls_uploads, formData, {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "Content-Type": "multipart/form-data",
          },
        });
        if (uploadRes?.data?.url) {
          const imageUrl = uploadRes.data.url;
          setFlyerData((prev) => {
            const existing = Array.isArray(prev.photos)
              ? prev.photos.filter((p) => p && p !== imageUrl)
              : [];
            const nextPhotos = [imageUrl, ...existing].filter(Boolean).slice(0, 1);
            const next = { ...prev, secondaryImage: imageUrl, photos: nextPhotos };
            onDirtyChange?.(true);
            onChange?.({ secondaryImage: imageUrl, photos: nextPhotos });
            return next;
          });
          toast({ title: "Success", description: "Secondary photo uploaded and saved", duration: 1000 });
        }
      } catch (err) {
        console.error("Failed to upload secondary image:", err);
        toast({ title: "Upload Error", description: "Failed to upload secondary photo to server", variant: "destructive" });
        setFlyerData((prev) => {
          const existing = Array.isArray(prev.photos)
            ? prev.photos.filter((p) => p && p !== dataUrl)
            : [];
          const nextPhotos = [dataUrl, ...existing].filter(Boolean).slice(0, 1);
          onDirtyChange?.(true);
          onChange?.({ secondaryImage: dataUrl, photos: nextPhotos });
          return { ...prev, secondaryImage: dataUrl, photos: nextPhotos };
        });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleRemovePhoto = () => {
    if (readOnly) return;
    setFlyerData((prev) => ({ ...prev, heroImage: "" }));
    onDirtyChange?.(true);
    onChange?.({ heroImage: "" });
    toast({ title: "Image Removed", description: "Property image has been removed" });
  };

  const selectedList =
    localSelectedListOverride ??
    viewSelectedList ??
    externalSelectedList ??
    selectedPropertyList ??
    property?.propertyListType?.list_name ??
    "New Listing";

  const allLists = mergePropertyListsWithCurrent(
    localPropertyLists.length > 0
      ? localPropertyLists
      : [
        { id: 1, list_name: "New Listing" },
        { id: 2, list_name: "Price Reduced" },
        { id: 3, list_name: "Coming Soon" },
        { id: 4, list_name: "Open House" },
      ],
    property?.propertyListType
  );

  const listSelectValue =
    resolvePropertyListName(
      selectedList,
      property?.propertyListType?.list_name
    ) || "New Listing";

  const [isOpenHouseConfirmed, setIsOpenHouseConfirmed] = useState<boolean>(
    property?.is_open_house ?? false
  );

  const handleOpenHouseChoice = async (confirmed: boolean) => {
    if (readOnly) return;
    if (!property?.id || selectedList !== "Open House") return;
    setIsOpenHouseConfirmed(confirmed);
    try {
      await axiosInstance.patch(`${endpoints.properties}${property.id}/`, {
        is_open_house: confirmed,
      });
      toast({
        title: confirmed ? "Open House Active" : "Open House Disabled",
        description: confirmed ? "Special styling enabled" : "Open House mode turned off",
        variant: "default",
      });
    } catch (err) {
      toast({ title: "Error", description: "Failed to save choice", variant: "destructive" });
      setIsOpenHouseConfirmed(!confirmed);
    }
  };

  useEffect(() => {
    setIsOpenHouseConfirmed(property?.is_open_house ?? false);
  }, [property?.is_open_house]);

  const saveCustomList = async () => {
    if (readOnly) return;
    const trimmedName = customListName.trim();
    if (!trimmedName) {
      toast({ title: "Error", description: "Please enter a name for your custom tag", variant: "destructive" });
      return;
    }
    if (!property?.id) {
      toast({ title: "Error", description: "No property selected. Please select a property first.", variant: "destructive" });
      return;
    }
    const existingList = localPropertyLists.find(
      (l) => l.list_name.toLowerCase() === trimmedName.toLowerCase()
    );
    if (existingList) {
      setLocalSelectedListOverride(existingList.list_name);
      updatePropertyList(existingList.list_name, existingList.id);
      setIsCustomList(false);
      setCustomListName("");
      toast({ title: "List Selected", description: `"${existingList.list_name}" already exists and has been selected` });
      return;
    }
    setIsSavingCustomList(true);
    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const createResponse = await axiosInstance.post(
        endpoints.propertylisttype,
        { list_name: trimmedName },
        { headers }
      );
      const newListType: PropertyList = {
        id: createResponse.data.id,
        list_name: createResponse.data.list_name,
      };
      setLocalSelectedListOverride(newListType.list_name);
      setLocalPropertyLists((prev) => {
        const exists = prev.some((l) => l.id === newListType.id);
        if (exists) return prev;
        return [...prev, newListType];
      });
      setIsCustomList(false);
      setCustomListName("");
      onPropertyListCreated?.(newListType);
      // Pass the freshly created id explicitly — propertyLists in the parent may not
      // have this brand-new entry yet, so a name-only lookup there could miss it.
      updatePropertyList(newListType.list_name, newListType.id);
      toast({ title: "Custom Tag Created", description: `"${newListType.list_name}" has been created and selected` });
    } catch (err: any) {
      console.error("Failed to create custom list:", err);
      const errorMessage =
        err?.response?.data?.list_name?.[0] ||
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Failed to create custom tag";
      toast({ title: "Error", description: errorMessage, variant: "destructive" });
    } finally {
      setIsSavingCustomList(false);
    }
  };

  const cancelCustomList = () => {
    setIsCustomList(false);
    setCustomListName("");
  };

  const handleListSelection = (value: string) => {
    if (readOnly) return;
    if (value === "__custom__") {
      setIsCustomList(true);
      setCustomListName("");
      return;
    }
    setLocalSelectedListOverride(value);
    // Persistence (the PATCH request) happens once, upstream in the parent's
    // onPropertyListChange handler — see updatePropertyList in TemplateBase.
    updatePropertyList(value);
  };

  // ─── Derived price font class ───────────────────────────────────────────────
  const priceFontClass = getPriceFontClass(flyerData.price);

  return (
    <div
      className="overflow-x-auto w-full"
      style={{ WebkitOverflowScrolling: "touch", transform: "rotateX(180deg)" }}
    >
      <div
        className="bg-[#f5f5f0] shadow-none rounded-lg overflow-hidden font-arial relative pb-2 border-0 mx-auto align-top"
        style={{ width: "100%", minWidth: "700px", transform: "rotateX(180deg)", border: "none", boxShadow: "none" }}
      >

        {/* ── Main Grid ── */}
        <div className="grid" style={{ gridTemplateColumns: "350px minmax(320px, 1fr)", gap: "0" }}>

          {/* ── LEFT: Hero Image ── */}
          <div
            className="relative bg-[#7c9a7c]/10 group overflow-hidden"
            style={{
              width: "350px",
              height: "550px",
              minWidth: "350px",
              maxWidth: "350px",
              minHeight: "550px",
              maxHeight: "550px",
            }}
          >

            {/* Resolution Hint - only when NO image and in edit mode */}
            {!readOnly && (
              <div className="absolute bottom-[92px] left-3 z-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                <div className="bg-black/70 text-white rounded-xl px-3 py-2 backdrop-blur-sm border border-white/10"
                  style={{
                    fontSize: "12px",
                    maxWidth: "320px",
                  }}
                >
                  {boxHeroRes ? (
                    <div style={{ lineHeight: "1.4" }}>
                      <div style={{ fontWeight: "600" }}>Required image resolution: {boxHeroRes}</div>
                      <div style={{ marginTop: "4px", fontSize: "11px" }}>Please upload exactly this resolution.</div>
                    </div>
                  ) : (
                    <div style={{ lineHeight: "1.4" }}>{RESOLUTION_HINT}</div>
                  )}
                </div>
              </div>
            )}

            {/* Open House Badge */}
            {isOpenHouseConfirmed && (
              <div className="absolute top-1 left-1 z-20"
                style={{
                  marginTop: "55px",
                  marginLeft: "-10px",
                }}
              >
                <img
                  src={openHouseBadge}
                  alt="Open House"
                  className="drop-shadow-xl object-center"
                  style={{
                    width: "110px",
                    height: "auto",
                  }}
                />
                <span className="absolute inset-0 flex items-center justify-center text-center text-white font-bold"
                  style={{
                    fontSize: "15px",
                  }}
                >
                  OPEN<br />HOUSE
                </span>
              </div>
            )}

            {/* Hero Image */}
            {flyerData.heroImage ? (
              <>
                <img
                  src={flyerData.heroImage}
                  alt="Property"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {!readOnly && (
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-3">
                    <Button
                      size="sm"
                      onClick={() => fileInputRef.current?.click()}
                      className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      Change Image
                    </Button>
                    <Button
                      size="sm"
                      onClick={openEditImage}
                      className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                    >
                      <Crop className="h-4 w-4 mr-2" />
                      Edit Image
                    </Button>
                  </div>
                )}
              </>
            ) : !readOnly ? (
              <label
                htmlFor="hero-upload"
                className="absolute inset-0 flex flex-col items-center justify-center cursor-pointer hover:bg-[#7c9a7c]/20 transition-colors"
              >
                <ImageIcon className="h-20 w-20 text-[#7c9a7c] mb-4" />
                <p className="text-2xl font-light text-[#4a5d4a]">Upload Property Photo</p>
                <p className="text-sm text-[#7c9a7c] mt-2">Click to select image</p>
              </label>
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <ImageIcon className="h-20 w-20 text-[#7c9a7c] mb-4" />
                <p className="text-2xl font-light text-[#4a5d4a]">No Image</p>
              </div>
            )}

            {/* ── Tag / List Selector ── */}
            <div className="absolute top-4 left-4 z-10"
              style={{
                maxWidth: "calc(100% - 1.5rem)",
              }}
            >
              {readOnly ? (
                <div className="flex items-center gap-2 bg-[#7c9a7c] px-4 py-2 rounded-lg shadow-sm max-w-full"
                  style={{
                    padding: "6px 16px",
                  }}
                >
                  <Tag className="h-4 w-4 text-white flex-shrink-0" />
                  <span className="truncate text-white font-semibold uppercase tracking-wide"
                    style={{
                      fontSize: "14px",
                    }}
                  >
                    {listSelectValue || "Just Listed"}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2 bg-[#7c9a7c] px-4 py-2 rounded-lg shadow-sm max-w-full"
                  style={{
                    padding: "6px 16px",
                  }}
                >
                  <Tag className="h-4 w-4 text-white flex-shrink-0" />
                  {isCustomList ? (
                    <div className="flex items-center gap-2 min-w-0">
                      <input
                        type="text"
                        value={customListName}
                        onChange={(e) => setCustomListName(e.target.value.slice(0, 20))}
                        placeholder="Custom tag (max 20 chars)"
                        maxLength={20}
                        className="bg-white/20 text-white placeholder-white/60 px-2 py-1 rounded outline-none border border-white/30 focus:border-white/60"
                        style={{
                          fontSize: "14px",
                          width: "128px",
                        }}
                        autoFocus
                        disabled={isSavingCustomList}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !isSavingCustomList) {
                            e.preventDefault();
                            saveCustomList();
                          }
                          if (e.key === "Escape") cancelCustomList();
                        }}
                      />
                      <button
                        onClick={saveCustomList}
                        disabled={!customListName.trim() || isSavingCustomList}
                        className="p-1.5 bg-white/20 rounded hover:bg-white/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                        title="Save custom tag"
                      >
                        {isSavingCustomList ? (
                          <Loader2 className="h-4 w-4 text-white animate-spin" />
                        ) : (
                          <Check className="h-4 w-4 text-white" />
                        )}
                      </button>
                      <button
                        onClick={cancelCustomList}
                        disabled={isSavingCustomList}
                        className="p-1.5 bg-white/20 rounded hover:bg-white/30 disabled:opacity-50 transition-colors flex-shrink-0"
                        title="Cancel"
                      >
                        <X className="h-4 w-4 text-white" />
                      </button>
                    </div>
                  ) : (
                    <select
                      value={listSelectValue}
                      onChange={(e) => handleListSelection(e.target.value)}
                      className="bg-transparent text-white font-semibold border-none outline-none uppercase cursor-pointer tracking-wide"
                      style={{
                        fontSize: "14px",
                        maxWidth: "160px",
                      }}
                    >
                      {allLists
                        .filter((list) => list.list_name !== "Open House")
                        .map((list) => (
                          <option key={list.id} value={list.list_name} className="text-gray-900 bg-white">
                            {list.list_name}
                          </option>
                        ))}
                      <option value="__custom__" className="text-gray-900 bg-white font-medium">
                        + Add Custom Tag
                      </option>
                    </select>
                  )}
                </div>
              )}
            </div>

            {/* File input */}
            {!readOnly && (
              <input
                id="hero-upload"
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFileChange}
              />
            )}

            {!readOnly && (
              <ImageCropModal
                open={cropModal.open}
                imageUrl={cropModal.imageUrl}
                title="Adjust Property Photo"
                outputSize={700}
                cropW={EDIT_CROP_W}
                cropH={EDIT_CROP_H}
                onConfirm={handleEditImageConfirm}
                onCancel={handleEditImageCancel}
              />
            )}

            {/* ── Bottom Stats Overlay ── */}
            <div className="absolute bottom-0 left-0 right-0 bg-[rgba(0,0,0,0.4)]">
              <div className="grid divide-x divide-white/30 text-white" style={{ gridTemplateColumns: "1fr 1fr 1fr" }}>
                {[
                  { val: flyerData.bedrooms, label: "Beds", field: "bedrooms" },
                  { val: flyerData.bathrooms, label: "Baths", field: "bathrooms" },
                  { val: flyerData.sqft, label: "Sq Ft", field: "sqft" },
                ].map((item, i) => (
                  <div key={item.field} className="px-4 py-4 text-center min-w-0">
                    {readOnly ? (
                      <p className="font-light text-white leading-tight w-full text-center overflow-hidden"
                        style={{
                          fontSize: (() => {
                            const len = String(item.val ?? 0).replace(/,/g, "").length;
                            if (len <= 3) return "1.75rem";
                            if (len <= 4) return "1.5rem";
                            if (len <= 5) return "1.25rem";
                            if (len <= 6) return "1rem";
                            return "0.8rem";
                          })()
                        }}
                      >
                        {item.val != null ? Number(item.val).toLocaleString() : 0}
                      </p>
                    ) : (
                      /* ── EDITABLE: constrained input ── */
                      <input
                        type="number"
                        min="0"
                        value={item.val ?? ""}
                        onChange={(e) =>
                          handleInputChange(
                            item.field as keyof Template5FlyerData,
                            e.target.value ? Math.max(0, Number(e.target.value)) : null
                          )
                        }
                        className="font-light w-full text-center bg-transparent border-none outline-none text-white"
                        style={{
                          fontSize: "24px",
                          WebkitAppearance: "none",
                          MozAppearance: "textfield",
                        }}
                        placeholder="0"
                      />
                    )}
                    <p className="uppercase tracking-widest opacity-80 mt-1"
                      style={{
                        fontSize: "12px",
                      }}
                    >
                      {item.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── RIGHT: Content ── */}
          <div className="px-3 sm:px-6 md:px-8 py-4 sm:py-5 md:py-6 flex flex-col h-full bg-[#f5f5f0] min-w-0 overflow-y-auto"
            style={{
              minHeight: "550px",
              minWidth: "320px",
            }}
          >
            <div className="min-w-0">

              {/* ── Price ── */}
              {readOnly ? (
                <p className={`font-light text-[#4a5d4a] mb-1 sm:mb-2 break-words leading-tight ${priceFontClass}`}>
                  {formatPrice(flyerData.price)}
                </p>
              ) : (
                <input
                  type="text"
                  value={
                    flyerData.price != null
                      ? `$${Number(flyerData.price).toLocaleString()}`
                      : ""
                  }
                  onChange={(e) => {
                    const num = e.target.value.replace(/[^0-9]/g, "");
                    handleInputChange("price", num === "" ? null : Number(num));
                  }}
                  className={`font-light text-[#4a5d4a] w-full bg-transparent border-none outline-none mb-1 sm:mb-2 placeholder-[#4a5d4a]/30 break-words min-w-0 ${priceFontClass}`}
                  placeholder="$485,000"
                />
              )}

              {/* ── Headline ── */}
              {readOnly ? (
                <p className="font-light text-[#6b6b6b] mb-2 sm:mb-3 italic break-words"
                  style={{
                    fontSize: "clamp(16px, 5vw, 20px)",
                  }}
                >
                  {flyerData.headline || "Contemporary Living at Its Finest"}
                </p>
              ) : (
                <textarea
                  value={flyerData.headline}
                  onChange={(e) => handleInputChange("headline", e.target.value)}
                  className="font-light text-[#6b6b6b] w-full bg-transparent border-none outline-none mb-2 sm:mb-3 italic placeholder-[#6b6b6b]/40 resize-none"
                  style={{
                    fontSize: "clamp(16px, 5vw, 20px)",
                  }}
                  placeholder="Contemporary Living at Its Finest"
                  rows={2}
                />
              )}

              {/* ── Address ── */}
              <div className="flex items-start mb-3 sm:mb-4 text-[#7c9a7c] min-w-0 gap-1.5">
                <MapPin className="h-4 sm:h-5 w-4 sm:w-5 mt-0.5 flex-shrink-0" />
                {readOnly ? (
                  <p className="break-words min-w-0 flex-1"
                    style={{
                      fontSize: "clamp(13px, 3.5vw, 14px)",
                    }}
                  >
                    {flyerData.address || "No address provided"}
                  </p>
                ) : (
                  <input
                    type="text"
                    value={flyerData.address}
                    onChange={(e) => handleInputChange("address", e.target.value)}
                    className="w-full bg-transparent border-none outline-none placeholder-[#7c9a7c]/50 min-w-0"
                    style={{
                      fontSize: "clamp(13px, 3.5vw, 14px)",
                    }}
                    placeholder="123 Main Street, Atlanta, GA 30303"
                  />
                )}
              </div>

              <div className="h-px bg-[#7c9a7c]/30 mb-3 sm:mb-4" />

              {/* ── Description ── */}
              {readOnly ? (
                <p className="leading-relaxed text-[#5a5a5a] mb-3 sm:mb-4 whitespace-pre-wrap break-words"
                  style={{
                    fontSize: "clamp(13px, 3.5vw, 14px)",
                  }}
                >
                  {flyerData.description || "No description provided."}
                </p>
              ) : (
                <textarea
                  value={flyerData.description}
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  className="w-full bg-transparent border-none outline-none resize-none leading-relaxed text-[#5a5a5a] mb-3 sm:mb-4 placeholder-[#5a5a5a]/40"
                  style={{
                    fontSize: "clamp(13px, 3.5vw, 14px)",
                  }}
                  rows={5}
                  placeholder="Discover contemporary elegance in this thoughtfully designed residence..."
                />
              )}

              {/* ── Key Features ── */}
              <h4 className="text-[#7c9a7c] mb-2 sm:mb-3 font-semibold flex items-center justify-between"
                style={{
                  fontSize: "clamp(11px, 3vw, 12px)",
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                }}
              >
                <span>Key Features</span>
                {!readOnly && (
                  <span className="text-muted-foreground font-normal"
                    style={{
                      fontSize: "clamp(11px, 3vw, 12px)",
                    }}
                  >
                    {flyerData.features.length}/10
                  </span>
                )}
              </h4>

              <div className="grid gap-1.5 sm:gap-2 mb-3 sm:mb-4"
                style={{
                  gridTemplateColumns: "1fr",
                }}
              >
                {flyerData.features.map((f, i) => (
                  <div
                    key={f.id ?? i}
                    className="grid items-center gap-1.5 sm:gap-2 text-sm text-[#5a5a5a] hover:bg-[#7c9a7c]/5 rounded px-1.5 sm:px-2 py-0.5 sm:py-1 transition-colors"
                    style={{
                      gridTemplateColumns: "auto 1fr auto",
                    }}
                  >
                    <div className="w-1.5 h-1.5 bg-[#7c9a7c] rounded-full flex-shrink-0" />
                    {readOnly ? (
                      <span className="break-words"
                        style={{
                          fontSize: "clamp(13px, 3.5vw, 14px)",
                        }}
                      >
                        {f.feature_name || "Feature"}
                      </span>
                    ) : (
                      <>
                        <input
                          type="text"
                          value={f.feature_name}
                          onChange={(e) => handleFeatureChange(i, e.target.value)}
                          onBlur={() => handleFeatureBlur(i)}
                          maxLength={30}
                          className="bg-transparent border-none outline-none w-full min-w-0 placeholder-[#5a5a5a]/40"
                          style={{
                            fontSize: "clamp(13px, 3.5vw, 14px)",
                          }}
                          placeholder="Hardwood Floors (max 30 chars)..."
                        />
                        <button
                          onMouseDown={(e) => {
                            e.preventDefault();
                            handleFeatureDelete(i);
                          }}
                          onTouchStart={(e) => {
                            e.preventDefault();
                            handleFeatureDelete(i);
                          }}
                          className="text-red-600 hover:text-red-800 flex-shrink-0 text-lg sm:text-xl"
                          aria-label="Delete feature"
                        >
                          ×
                        </button>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Add Feature Button */}
              {!readOnly && flyerData.features.length < 10 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addFeature}
                  className="w-full text-[#7c9a7c] border-[#7c9a7c]/30 hover:bg-[#7c9a7c]/10 hover:border-[#7c9a7c] transition-all"
                >
                  + Add Feature
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* ── Agent Section ── */}
        <div className="border-t border-[#7c9a7c]/30 bg-[#f5f5f0] p-3 sm:p-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-3 sm:gap-4 md:gap-6">

              {/* Agent Avatar */}
              <div className="flex-shrink-0">
                <div className="rounded-lg overflow-hidden" style={{ width: "clamp(100px, 15vw, 150px)", height: "clamp(120px, 18vw, 180px)" }}>
                  <img
                    src={agentData?.avatar || profile}
                    alt="Agent"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* Agent Details */}
              <div className="min-w-0 text-center py-1 sm:py-2">
                <p className="text-xs sm:text-sm md:text-base font-bold text-[#4a5d4a] break-words mb-1.5 sm:mb-2">
                  {flyerData.agentName || agentData?.first_name || "Agent Name"}
                </p>

                <div className="flex flex-col items-center gap-1 sm:gap-1.5 w-full">
                  <div className="flex items-center gap-1 sm:gap-1.5 justify-center min-w-0">
                    <Phone className="w-3 sm:w-4 h-3 sm:h-4 text-[#9db39d] flex-shrink-0" />
                    <span className="text-xs sm:text-sm md:text-base text-[#6b6b6b] break-all">
                      {flyerData.agentPhone || agentData?.phone_number || "N/A"}
                    </span>
                  </div>

                  <div className="flex items-center gap-1 sm:gap-1.5 justify-center min-w-0">
                    <Mail className="w-3 sm:w-4 h-3 sm:h-4 text-[#9db39d] flex-shrink-0" />
                    <span className="text-xs sm:text-sm md:text-base text-[#6b6b6b] break-all underline ">
                      {/* {flyerData.agentEmail || agentData?.email || "N/A"} */}
                       Click here to email Me
                    </span>
                  </div>

                  <div className="flex items-center gap-1 sm:gap-1.5 justify-center min-w-0">
                    <Building2 className="w-3 sm:w-4 h-3 sm:h-4 text-[#9db39d] flex-shrink-0" />
                    <span className="text-xs sm:text-sm md:text-base text-[#6b6b6b] break-all">
                      {flyerData.company || agentData?.company || "N/A"}
                    </span>
                  </div>

                  {/* License Number */}
                  {agentData?.license_number && (
                    <div className="flex items-center gap-1 sm:gap-1.5 justify-center min-w-0">
                      <IdCard className="w-3 sm:w-4 h-3 sm:h-4 text-[#9db39d] flex-shrink-0" />
                      <span className="text-xs sm:text-sm md:text-base text-[#6b6b6b] break-all">
                        License #: {agentData.license_number}
                      </span>
                    </div>
                  )}

                </div>
              </div>

              {/* Company Logo */}
              <div className="flex-shrink-0">
                <div className="rounded-lg overflow-hidden" style={{ width: "clamp(100px, 15vw, 180px)", height: "clamp(120px, 18vw, 180px)" }}>
                  <img
                    src={agentData?.company_logo}
                    alt="Company"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


